import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Menu,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  Anchor,
  Loader,
  Center,
} from '@mantine/core';
import { CleanTabs } from '@ayocore/platform/client/components/CleanTabs';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import type { ColumnDef } from '@tanstack/react-table';
import {
  IconArchive,
  IconArchiveOff,
  IconDots,
  IconEdit,
  IconLink,
  IconTrash,
} from '@tabler/icons-react';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { notifications } from '@mantine/notifications';
import { DealView } from '@ayocore/erp/crm/client/components/Deals/views/DealView';
import { PartnerSelect } from '@ayocore/erp/crm/client/components/Partners/PartnerSelect';
import RepositoryTable from '@ayocore/platform/client/components/RepositoryTable/RepositoryTable';
import { MoneyCell, DateCell, TextCell } from '@ayocore/platform/client/components/RTable/cells';
import { useTab } from '@ayocore/platform/client/tabs';
import type { AccountingDocument } from '@ayocore/erp/accounting/schemas/document.schema';
import type { AccountingDocumentTransaction } from '@ayocore/erp/accounting/schemas/document-transaction.schema';
import {
  useSupplierDocRepository,
  useAccountingDocumentTransactionRepository,
} from '@ayocore/erp/accounting/client/rpc';
import { LinkDealForm } from '../LinkDealForm';
import { EditSupplierDocForm } from '../EditSupplierDocForm';
import { DocumentAttributesPanel } from '../../DocumentAttributes/DocumentAttributesPanel';
import { AllocationTypeSelect } from '../../AllocationTypeSelect';
import logger from '@ayocore/platform/common/helpers/logger';

type SupplierDoc = AccountingDocument & {
  partner?: { id: number; name?: string | null } | null;
  owner_user?: { id: number; name?: string | null } | null;
  /** Сумма уже привязанных транзакций (withExpensesSum). */
  expenses_sum?: number;
};

interface SupplierDocumentViewProps {
  documentId?: number;
  isModal?: boolean;
  /** Вызывается после успешного удаления документа из drawer-режима. */
  onDeleted?: () => void;
}

export function SupplierDocumentView({
  documentId: propDocumentId,
  isModal = false,
  onDeleted,
}: SupplierDocumentViewProps) {
  const { id } = useParams<{ id: string }>();
  const { setTabTitle } = useTab();

  const documentId = propDocumentId || (id ? parseInt(id, 10) : null);

  const supplierDocRepository = useSupplierDocRepository();
  const transactionRepository = useAccountingDocumentTransactionRepository();
  const linkDealModal = useModal(LinkDealForm);
  const editModal = useModal(EditSupplierDocForm);
  const navigate = useNavigate();
  const dealViewDrawer = useDrawer(DealView, {
    openInTab: props => navigate(`/crm/deals/${props.dealId}`),
  });

  const [doc, setDoc] = useState<SupplierDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState<{ allowed: boolean; message?: string }>({
    allowed: false,
  });
  const [activeTab, setActiveTab] = useState<string | null>('main');

  linkDealModal.onClose(() => setRefreshTrigger(prev => prev + 1));
  editModal.onClose(() => setRefreshTrigger(prev => prev + 1));

  const loadDocument = useCallback(async () => {
    if (!documentId) return;
    try {
      setLoading(true);
      setError(null);
      const [result, canUpdateResult, canDeleteResult] = await Promise.all([
        supplierDocRepository.withBaseRelations().withExpensesSum().findById(documentId),
        supplierDocRepository.canUpdate(documentId),
        supplierDocRepository.canDelete(documentId),
      ]);
      setDoc(result as SupplierDoc);
      setCanEdit(canUpdateResult.allowed);
      setCanDelete(canDeleteResult);
      if (!isModal) {
        setTabTitle(`Документ №${result?.number || `#${documentId}`}`);
      }
    } catch (err) {
      logger.error('Ошибка загрузки документа:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить документ');
    } finally {
      setLoading(false);
    }
  }, [documentId, supplierDocRepository, isModal, setTabTitle]);

  useEffect(() => {
    void loadDocument();
  }, [loadDocument]);

  /**
   * Удаление документа поставщика. Подтверждение через confirm() — единый паттерн с GrasiasDealExpenses.
   * Сервер всё равно повторно проверит блокировки (onBeforeDelete), поэтому здесь — только отображение
   * результата. ADMIN-bypass обходит UI-проверку canDelete, но onBeforeDelete сработает у всех
   * — этот race-кейс ловим в catch.
   */
  const handleDelete = useCallback(async () => {
    if (!doc) return;
    const label = doc.number || `#${doc.id}`;
    const confirmText = doc.number ? `Удалить документ №${label}?` : `Удалить документ ${label}?`;
    if (!window.confirm(confirmText)) return;
    try {
      await supplierDocRepository.deleteById(doc.id as number);
      notifications.show({ color: 'green', message: 'Документ удалён' });
      if (isModal) {
        onDeleted?.();
      } else {
        navigate('/accounting/supplier-docs');
      }
    } catch (err) {
      logger.error('Ошибка удаления документа поставщика:', err);
      notifications.show({
        color: 'red',
        message: err instanceof Error ? err.message : 'Не удалось удалить документ',
      });
    }
  }, [doc, isModal, onDeleted, supplierDocRepository, navigate]);

  /**
   * Архивировать / разархивировать документ. Архив — только признак скрытия в
   * списках (`archived_at`), учётного смысла не несёт. Дату ставит сервер
   * (методы archive/unarchive), `archived_by_id` проставляет rule document_rules.
   * Идёт через update() — срабатывают системные хуки.
   */
  const handleToggleArchive = useCallback(async () => {
    if (!doc) return;
    const isArchived = Boolean(doc.archived_at);
    try {
      if (isArchived) {
        await supplierDocRepository.unarchive(doc.id as number);
        notifications.show({ color: 'teal', message: 'Документ возвращён из архива' });
      } else {
        await supplierDocRepository.archive(doc.id as number);
        notifications.show({ color: 'teal', message: 'Документ архивирован' });
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      logger.error('Ошибка изменения архивного статуса документа:', err);
      notifications.show({
        color: 'red',
        message: err instanceof Error ? err.message : 'Не удалось изменить архивный статус',
      });
    }
  }, [doc, supplierDocRepository]);

  // Перезагрузка документа при обновлении транзакций
  useEffect(() => {
    if (refreshTrigger > 0) {
      void loadDocument();
    }
  }, [refreshTrigger, loadDocument]);

  const txRepository = useMemo(
    () =>
      documentId
        ? transactionRepository
            .filterByDocument(documentId)
            .withBaseRelations()
            .withDeal()
            .orderBy('id', 'desc')
        : null,
    [transactionRepository, documentId]
  );

  const txColumns = useMemo(
    (): ColumnDef<AccountingDocumentTransaction>[] => [
      {
        id: 'deal.number',
        header: 'Сделка',
        accessorFn: row => {
          const deal = row.deal as { id: number; number?: string } | null;
          return deal?.number ?? '';
        },
        size: 140,
        cell: ({ row }) => {
          const dealId = row.original.deal_id;
          if (!dealId)
            return (
              <Text size='xs' c='dimmed'>
                —
              </Text>
            );
          const deal = row.original.deal as { id: number; number?: string } | null;
          return (
            <Anchor
              component='button'
              type='button'
              size='xs'
              onClick={e => {
                e.stopPropagation();
                dealViewDrawer.open({ dealId, isModal: true });
              }}
            >
              {deal?.number || `#${dealId}`}
            </Anchor>
          );
        },
      },
      {
        id: 'amount',
        header: 'Сумма',
        size: 140,
        accessorKey: 'amount',
        cell: ({ row }) => <MoneyCell value={row.original.amount} />,
      },
      {
        id: 'operation_date',
        header: 'Дата операции',
        size: 130,
        accessorKey: 'operation_date',
        cell: ({ row }) => <DateCell value={row.original.operation_date} />,
      },
      {
        id: 'description',
        header: 'Комментарий',
        size: 250,
        accessorKey: 'description',
        cell: ({ row }) => <TextCell value={row.original.description} />,
      },
    ],
    []
  );

  if (!documentId || isNaN(documentId)) {
    return (
      <Alert color='red' title='Ошибка'>
        Неверный ID документа
      </Alert>
    );
  }

  if (loading) {
    return (
      <Center py='xl'>
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color='red' title='Ошибка'>
        {error}
      </Alert>
    );
  }

  if (!doc) {
    return (
      <Alert color='yellow' title='Не найден'>
        Документ не найден
      </Alert>
    );
  }

  const formatMoney = (v: number | null | undefined) =>
    v != null
      ? new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2 }).format(Number(v))
      : '—';

  const formatDate = (v: string | Date | null | undefined) => {
    if (!v) return '—';
    const d = v instanceof Date ? v : new Date(v);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU');
  };

  const docLabel = doc.number || `#${doc.id}`;

  return (
    <Stack gap='md' p={isModal ? 0 : 'md'}>
      {/* Шапка документа */}
      <Group justify='space-between' align='flex-start'>
        <Text fw={600} size='lg'>
          {doc.archived_at && (
            <Text span c='dimmed'>
              [Архивный]{' '}
            </Text>
          )}
          Документ №{docLabel}
        </Text>
        <Menu withArrow position='bottom-end' withinPortal>
          <Menu.Target>
            <ActionIcon variant='light' size='xs' aria-label='Действия'>
              <IconDots size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {canEdit && (
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={() => {
                  editModal.open(
                    {
                      title: `Редактировать — ${docLabel}`,
                      documentId: doc.id as number,
                      onSaved: () => {
                        editModal.close();
                        setRefreshTrigger(prev => prev + 1);
                      },
                    },
                    { size: 'md' }
                  );
                }}
              >
                Редактировать
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={
                doc.archived_at ? <IconArchiveOff size={14} /> : <IconArchive size={14} />
              }
              onClick={handleToggleArchive}
            >
              {doc.archived_at ? 'Разархивировать' : 'Архивировать'}
            </Menu.Item>
            {canDelete.allowed && (
              <Menu.Item leftSection={<IconTrash size={14} />} color='red' onClick={handleDelete}>
                Удалить
              </Menu.Item>
            )}
            {!canDelete.allowed && canDelete.message && (
              // Tooltip требует обёртки <span> над disabled-элементом: HTML-стандартно
              // disabled-элементы не пропускают mouse-события, поэтому без обёртки tooltip не сработает.
              <Tooltip label={canDelete.message} withArrow position='left'>
                <span>
                  <Menu.Item leftSection={<IconTrash size={14} />} color='red' disabled>
                    Удалить
                  </Menu.Item>
                </span>
              </Tooltip>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>

      <ViewPanel>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing='sm'>
          <InfoRow>
            <InfoRow.TitleCol>Номер</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              <Text size='xs' fw={600}>
                {docLabel}
              </Text>
            </InfoRow.ValueCol>
          </InfoRow>
          <InfoRow>
            <InfoRow.TitleCol>Дата</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              <Text size='xs'>{formatDate(doc.operation_date)}</Text>
            </InfoRow.ValueCol>
          </InfoRow>
          <InfoRow>
            <InfoRow.TitleCol>Статус</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              <Badge
                color={doc.posted ? 'green' : 'gray'}
                variant={doc.posted ? 'filled' : 'light'}
                size='sm'
              >
                {doc.posted ? 'Проведён' : 'Черновик'}
              </Badge>
            </InfoRow.ValueCol>
          </InfoRow>
          <InfoRow>
            <InfoRow.TitleCol>Партнёр</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              {doc.partner_id ? (
                <Text size='xs'>{doc.partner?.name || '—'}</Text>
              ) : canEdit ? (
                <PartnerSelect
                  value=''
                  onChange={async value => {
                    if (!value) return;
                    try {
                      await supplierDocRepository.update(doc.id as number, {
                        partner_id: parseInt(value, 10),
                      });
                      setRefreshTrigger(prev => prev + 1);
                    } catch (err) {
                      notifications.show({
                        color: 'red',
                        message: err instanceof Error ? err.message : 'Ошибка привязки партнёра',
                      });
                    }
                  }}
                />
              ) : (
                <Text size='xs' c='dimmed'>
                  Не указан
                </Text>
              )}
            </InfoRow.ValueCol>
          </InfoRow>
          <InfoRow>
            <InfoRow.TitleCol>Сумма с НДС</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              <Text size='xs' fw={500}>
                {formatMoney(doc.total_amount)} ₽
              </Text>
            </InfoRow.ValueCol>
          </InfoRow>
          <InfoRow>
            <InfoRow.TitleCol>НДС</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              <Text size='xs'>{formatMoney(doc.tax_amount)} ₽</Text>
            </InfoRow.ValueCol>
          </InfoRow>
        </SimpleGrid>
        {doc.description && (
          <InfoRow>
            <InfoRow.TitleCol>Комментарий</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              <Text size='xs'>{doc.description}</Text>
            </InfoRow.ValueCol>
          </InfoRow>
        )}
      </ViewPanel>

      <CleanTabs value={activeTab} onChange={setActiveTab}>
        <CleanTabs.List>
          <CleanTabs.Tab value='main'>Основное</CleanTabs.Tab>
          <CleanTabs.Tab value='extra'>Дополнительно</CleanTabs.Tab>
        </CleanTabs.List>

        <CleanTabs.Panel value='main'>
          {txRepository && (
            <RepositoryTable<AccountingDocumentTransaction>
              id={`supplier-doc-transactions-${documentId}`}
              repository={txRepository}
              columns={txColumns}
              showSearch={false}
              refreshTrigger={refreshTrigger}
              rowHeight={1}
              tableBodyHeight='calc(100vh - 380px)'
              aggregates={[{ fn: 'sum', field: 'amount', label: 'Итого привязано' }]}
              // Архивный документ к сделке не привязывают — кнопку скрываем
              // (инстансные правила могут запрещать это и на сервере).
              customActions={
                doc.archived_at
                  ? []
                  : [
                      <Button
                        key='link-deal'
                        size='xs'
                        variant='light'
                        leftSection={<IconLink size={14} />}
                        onClick={() => {
                          linkDealModal.open({
                            title: `Привязать сделку — ${docLabel}`,
                            documentId: doc.id as number,
                            documentAmount: doc.total_amount,
                            linkedAmount: doc.expenses_sum ?? null,
                            documentDescription: doc.description,
                            onLinked: () => {
                              linkDealModal.close();
                              setRefreshTrigger(prev => prev + 1);
                            },
                          });
                        }}
                      >
                        Привязать сделку
                      </Button>,
                    ]
              }
            />
          )}
        </CleanTabs.Panel>

        <CleanTabs.Panel value='extra'>
          <Stack gap='md'>
            <AllocationTypeSelect
              documentId={doc.id as number}
              value={(doc.allocation_type_id as number | null) ?? null}
              disabled={!canEdit}
              onChanged={() => setRefreshTrigger(prev => prev + 1)}
            />
            <DocumentAttributesPanel
              documentId={doc.id as number}
              documentTypeCode='SUPPLIER_DOC'
            />
          </Stack>
        </CleanTabs.Panel>
      </CleanTabs>
    </Stack>
  );
}
